import { NextPage, NextPageContext } from 'next';
import React from 'react';

import { setRentals } from '@/actions/rentals';
import { IPageError } from '@/components/route/campground/campgroundTypes';
import RvBundlesTemplate from '@/components/templates/RvBundlesTemplate';
import { REVIEWS_LIMIT } from '@/constants/listing';
import { getRvBundlesListing } from '@/redux/modules/campgroundListing';
import { getReviews } from '@/redux/modules/reviews';
import { redirectToSearch, withRedirectToSearch } from '@/services/redirectToSearch';
import { ICampgroundData } from '@/services/types/search/campgrounds/id';
import { isSSR } from '@/utility/isSSR';
import { IStore } from '@/utility/redux/store';
import { getSessionCookie } from '@/utility/session';

export interface IProps {
  error?: IPageError | null;
}

const ListingPage: NextPage<IProps> = ({ error }) => {
  return <RvBundlesTemplate error={error} />;
};

ListingPage.getInitialProps = withRedirectToSearch<IProps>(
  async ({ res, req, query, pathname, reduxStore }: NextPageContext & { reduxStore: IStore }) => {
    const { slug, preview, ...remainingParams } = query;

    // campground.vacation_package_slug has NOT been renamed to escapes yet!
    const escapesSlug = `/vacation-packages/${slug}`;
    const state = reduxStore.getState();
    const hasListingData = state.campgroundListing.data?.vacation_package_slug === escapesSlug;
    const hasReviewsData = hasListingData && !!state.reviews.all.data?.length;
    reduxStore.dispatch(setRentals({ data: [] }));

    // get the campground listing but catch 404 errors from redux and redirect to search
    let campgroundListing: ICampgroundData | null = null;
    try {
      campgroundListing = hasListingData
        ? null
        : await reduxStore.dispatch(getRvBundlesListing(escapesSlug, remainingParams));
    } catch (error) {
      if (error.message === '404') {
        return redirectToSearch({ res, query });
      }

      throw error;
    }

    const campgroundData = state.campgroundListing.data || campgroundListing;

    if (campgroundData?.id && !hasReviewsData) {
      await reduxStore.dispatch(getReviews(`${campgroundData.id}`, true, { limit: REVIEWS_LIMIT }));
    }

    const listingSlug = campgroundData?.vacation_package_slug;

    // Force redirect if this is a non-campground listing (e.g. RV listing)
    // campground.vacation_package_slug has NOT been renamed to escapes yet!
    if (listingSlug && !listingSlug.startsWith('/vacation-packages')) {
      return redirectToSearch({ res, query });
    }

    // Force redirect if listing is hidden or unpublished
    if (campgroundData?.hidden || campgroundData?.has_been_published === false) {
      const cookie = req?.headers?.cookie;
      const auth = cookie && getSessionCookie(cookie);
      const userID = (auth && auth.authenticated?.user_id) || null;
      if (parseInt(userID) !== campgroundData.owner?.id || preview !== 'true') {
        return redirectToSearch({ res, query }, pathname);
      }
    }

    // Redirect to absolute URL
    if (listingSlug && listingSlug !== escapesSlug) {
      if (res) {
        res.writeHead(301, { Location: listingSlug });
        res.end();
      } else {
        if (window.top) {
          window.top.location.href = `${window.top.location?.origin}${listingSlug}`;
        }
      }
    }

    return isSSR() ? { error: null } : {};
  },
);

export default ListingPage;
